import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UUID } from "crypto";
import { Box, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import * as yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { signIn } from "aws-amplify/auth";
import {
  ChoiceToggle,
  ConsentRow,
  DialogConfirm,
  FieldWrapper,
  PageHeader,
  SectionHeader,
  StepActions,
} from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import {
  setAccountUserPhone,
  setClientDetailsData,
  setClientDetailsId,
  setId_application,
  set_Id_applicationCondition,
  setLegalRepresentative,
  setLoanId,
  setPersonalData,
} from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useRegisterUserMutation, useRequestPhoneCodeMutation } from "@api/onboarding";
import { useLazyGetSessionInfoQuery } from "@api/session";
import { useLazyGetPersonTypeQuery, useLazyGetStatesQuery } from "@api/catalogs";
import { usePatchCuentaMutation } from "@api/account";
import { usePatchPersonMutation, usePostPersonMutation } from "@api/personasApi";
import { useLazyGetTaxSystemConfigQuery, usePostValidateNextStageMutation } from "@api/applications";
import { useLazyGetLeasingListQuery, usePatchLeasingMutation, usePostLeasingMutation } from "@api/leasing";
import { LeasingPost, LeasingVehicleDetailsPost } from "@interfaces/LeasingTypes";
import { PayloadPersonPatch, PayloadRegisterUser } from "@interfaces/request";
import { StateStructure } from "@interfaces/catalogs";
import { ApiError, StatusCatalog } from "@interfaces/response";
import {
  MENSAJE_EDAD_MAXIMA,
  PATHS_COTIZADORES_V2,
  PERSON_CATALOG,
  STATUS_CATALOG,
  STATUS_SIN_COBERTURA,
} from "@helpers/constantes";
import {
  calcularCurp,
  calcularRfcPf,
  esFechaNacimientoDentroDeEdadMaxima,
  getFechaNacimientoMinima,
  getNextStatus,
  substractDateFromCURP,
  substractDateFromRFC,
  transformTitleCase,
  validateDates,
} from "@helpers/funciones";
import { CURP, EMAIL, LAST_NAMES, NAME, RFC_PFAE } from "@utils/expreciones";
import { useExistingPersonId } from "@hooks/useExistingPersonId";

const EMPTY_ID = "00000000-0000-0000-0000-000000000000";
const MAX_DATE = dayjs().subtract(18, "years");
const ERROR_INVALID_RFC_DATE = "La fecha de nacimiento del RFC no coincide con la que capturaste";
const ERROR_INVALID_CURP_DATE = "La fecha de nacimiento de la CURP no coincide con la que capturaste";
/** Código del registro cuando ya existe un usuario con el celular de la cuenta en la empresa. */
const ERROR_CELULAR_REGISTRADO = "002";
/** sessionStorage: datos capturados que se guardan solo con `ERROR_CELULAR_REGISTRADO`, para no recapturarlos. */
const DATOS_GUARDADOS_KEY = "tus-datos-wuwei";

type FormValues = {
  name: string;
  first_lastname: string;
  second_lastname: string;
  email: string;
  date_birth: string;
  birth_state: string;
  sex: string;
  rfc: string;
  curp: string;
  declaration: boolean;
};

const leerDatosGuardados = (): Partial<FormValues> => {
  try {
    return JSON.parse(sessionStorage.getItem(DATOS_GUARDADOS_KEY) ?? "{}");
  } catch {
    return {};
  }
};

const validationSchema = yup.object().shape({
  name: yup.string().required("Campo requerido").matches(new RegExp(NAME), "Nombre inválido, revisa el formato"),
  first_lastname: yup
    .string()
    .required("Campo requerido")
    .matches(new RegExp(LAST_NAMES), "Apellido paterno inválido, revisa el formato"),
  second_lastname: yup.string().matches(new RegExp(LAST_NAMES), "Apellido materno inválido, revisa el formato"),
  email: yup
    .string()
    .required("Campo requerido")
    .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
  date_birth: yup
    .string()
    .required("Campo requerido")
    .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
    .test("edad-maxima", MENSAJE_EDAD_MAXIMA, esFechaNacimientoDentroDeEdadMaxima)
    .test("is-adult", "Debes ser mayor de 18 años", (value) => !!value && dayjs().diff(dayjs(value), "years") >= 18),
  birth_state: yup.string().required("Campo requerido"),
  sex: yup.string().oneOf(["MASCULINO", "FEMENINO"], "Selecciona una opción").required("Campo requerido"),
  rfc: yup
    .string()
    .required("Campo requerido")
    .matches(RFC_PFAE, "Formato de RFC no válido: son 13 caracteres con homoclave")
    .test("fecha-nacimiento-rfc", ERROR_INVALID_RFC_DATE, function (value) {
      if (!value) return true;
      return validateDates(this.parent.date_birth, substractDateFromRFC(value));
    }),
  curp: yup
    .string()
    .required("Campo requerido")
    .matches(CURP, "Formato de CURP no válido")
    .test("fecha-nacimiento-curp", ERROR_INVALID_CURP_DATE, function (value) {
      if (!value) return true;
      return validateDates(this.parent.date_birth, substractDateFromCURP(value));
    }),
  declaration: yup.boolean().isTrue(),
});

/**
 * "Tus datos" de WuWei (`WW_Actividad` del diseño): reemplaza a `RegistroDatosPersonales` para WuWei (PF). Junta
 * en una pantalla el registro del legacy PF (`tofu/pm/RegistroDatosPersonales`) y los datos oficiales
 * (`tofu/pm/DatosOficiales`, sin estado civil). Al continuar registra al usuario (o actualiza, si ya existe) y avanza
 * a la pantalla que siga en el `status_config` del back.
 */
export const RegistroDatosPersonalesWuWei = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const statusConfig = useSelector((state: RootState) => state.app.status_config);

  const [registerUser] = useRegisterUserMutation();
  const [requestPhoneCode] = useRequestPhoneCodeMutation();
  const [getSessionInfo] = useLazyGetSessionInfoQuery();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getPersonTypes] = useLazyGetPersonTypeQuery();
  const [getStates] = useLazyGetStatesQuery();
  const [patchAccount] = usePatchCuentaMutation();
  const [postPerson] = usePostPersonMutation();
  const resolvePersonId = useExistingPersonId();
  const [patchPerson] = usePatchPersonMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getLeasingList] = useLazyGetLeasingListQuery();
  const [postLeasing] = usePostLeasingMutation();
  const [patchLeasing] = usePatchLeasingMutation();

  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  // Lo que capturó antes de que el registro rechazara su celular (`ERROR_CELULAR_REGISTRADO`), si regresó a cambiarlo.
  const [datosGuardados] = useState(leerDatosGuardados);
  const [celularRegistrado, setCelularRegistrado] = useState(false);
  const [states, setStates] = useState<StateStructure[]>([]);
  // PF: el cliente es "dueño" y la persona se da de alta como accionista mayoritario y representante legal (legacy).
  const [ownerTypeId, setOwnerTypeId] = useState("");
  const [personTypeIds, setPersonTypeIds] = useState<string[]>([]);
  const fechaNacimientoInicial = datosGuardados.date_birth || clientDetails.birth_date;
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    fechaNacimientoInicial ? dayjs(fechaNacimientoInicial) : null,
  );

  const isRegistered = clientDetails.id !== EMPTY_ID;
  // La cuenta se crea con el celular (`account_type=phone` del cotizador): es el usuario para iniciar sesión.
  //const isPhoneAccount = application.account_type === "phone";

  /** `crear_cuenta` necesita los query params de la cotización: igual que `RegistroDatosPersonalesMercader`. */
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
      return;
    }
    navigate(PATHS_COTIZADORES_V2.credito_simple.path);
  };

  /**
   * Datos oficiales, ya con la solicitud creada: cuenta (`DatosOficiales`) y persona del titular. Igual en el alta y
   * al volver a la pantalla.
   */
  const saveOfficialData = async (loanApplicationId: string, clientDetailsId: string, values: FormValues) => {
    const birth_date = dayjs(values.date_birth).format("YYYY-MM-DD");
    const birth_state = Number(values.birth_state);
    const sex = values.sex === "MASCULINO" ? "MASCULINO" : "FEMENINO";

    await patchAccount({
      accountId: clientDetailsId,
      body: { company_rfc: values.rfc, curp: values.curp, birth_state, birth_date, sex },
    }).unwrap();

    const payloadPerson: PayloadPersonPatch = {
      person_types: personTypeIds,
      name: values.name,
      last_name: values.first_lastname,
      last_name_2: values.second_lastname,
      loan_application_id: loanApplicationId,
      code_phone_number: accountUser.code_phone,
      phone_number: accountUser.phone,
      email: values.email,
      rfc: values.rfc,
      curp: values.curp,
      birth_date,
      birth_state,
      sex: values.sex,
      is_shareholder: true,
    };
    // Si ya existe (volvió a la pantalla, incluso tras iniciar sesión), se actualiza en vez de crear otra.
    const existingPersonId = await resolvePersonId(loanApplicationId);
    const responsePerson = existingPersonId
      ? await patchPerson({ personId: existingPersonId, body: payloadPerson }).unwrap()
      : await postPerson({ body: payloadPerson }).unwrap();
    dispatch(setLegalRepresentative(responsePerson.data));

    dispatch(
      setClientDetailsData({
        rfc: values.rfc,
        curp: values.curp,
        birth_state: values.birth_state,
        birth_date,
        sex: values.sex,
      }),
    );
  };

  /**
   * Leasing del auto, como `RegistroDatosPersonalesMercader`: si la solicitud ya tiene uno se actualiza (`patchLeasing`)
   * y si no se crea (`postLeasing`). La unidad y el color vienen del cotizador (`quote_vehicle`). Si el store ya no los
   * tiene (volvió a entrar con otra sesión) y el leasing existe, se deja como está.
   */
  const saveLeasing = async (loanApplicationId: string) => {
    const [existing] = await getLeasingList(loanApplicationId).unwrap();
    const vehicle = application.quote_vehicle;
    if (!vehicle) {
      if (existing?.id) return;
      throw new Error("No se encontró el vehículo de la cotización");
    }

    const leasing: LeasingPost<LeasingVehicleDetailsPost> = {
      loan_application_id: loanApplicationId,
      quantity: 1,
      // Precio de la unidad (IVA incluido) que se cotizó.
      estimated_value: application.loan_amount,
      requested_advance_percentage: application.advance_percentage || 0,
      requested_residual_percentage: application.residual_percentage || 0,
      details: {
        ...vehicle,
        description: `${vehicle.make} ${vehicle.model} ${vehicle.version} ${vehicle.year}, ${vehicle.color}`,
      },
    };
    if (existing?.id) {
      await patchLeasing({ id_solicitud: loanApplicationId, vehicleId: existing.id, data: leasing }).unwrap();
    } else {
      await postLeasing({ id_solicitud: loanApplicationId, data: { leasings_list: [leasing] } }).unwrap();
    }
  };

  /** Alta del usuario y de la solicitud PF, como el legacy; regresa la solicitud y el `status_config` de la sesión. */
  const register = async (values: FormValues): Promise<{ loanApplicationId: string; config: StatusCatalog[] }> => {
    const taxSystem = await getTaxSystemConfig(application.type).unwrap();
    // Usuario de la cuenta: el celular completo con lada (`+525512345678`) o, si la cuenta es por correo, el correo.
    //const username = isPhoneAccount ? (accountUser.phone_full ?? "") : values.email;
    const fullName = [values.name, values.first_lastname, values.second_lastname].filter(Boolean).join(" ");

    const payloadRegisterUser: PayloadRegisterUser = {
      username: values.email,
      email: values.email,
      password: accountUser.password,
      phone: accountUser.phone_full ?? "",
      rol_detail: "",
      rol_id: ownerTypeId,
      loan_application: {
        product_id: application.product_id as UUID,
        tax_systems_id: String(application.tax_system_id),
        source_code: application.source_code,
        financed_services_id: application.financed_services_id,
        guarantee_type_id: application.guarantee_type_id,
        description_guarantee: application.description_guarantee,
        guarantee_types: application.guarantee_types?.length ? application.guarantee_types : null,
        convenio_id: application.convenio_id ?? null,
        payroll_agreement_id: application.convenio_id ?? null,
        leasing_type_id: application.leasing_type_id ?? null,
        quoter_details: application.quoter_details ?? null,
      },
      application_condition: {
        requested_amount: application.loan_amount,
        requested_term: application.loan_term,
        selected_amount: application.loan_amount,
        selected_term: application.loan_term,
        approved_max_term: taxSystem.maxTerm,
        approved_min_term: taxSystem.minTerm,
        loan_amount: application.loan_amount,
        approved_amount: taxSystem.maxAmount,
      },
      client_details: {
        name: values.name,
        last_name: values.first_lastname,
        last_name_2: values.second_lastname,
        person_type_id: ownerTypeId,
        tax_systems_id: String(application.tax_system_id),
        is_previous_client: clientDetails.is_previous_client,
        company_rfc: values.rfc,
        company_name: fullName,
        // PF: el celular de la cuenta se guarda en el cliente.
        country_code: accountUser.code_phone,
        cell_phone_number: accountUser.phone,
      },
    };

    const responseRegister = await registerUser(payloadRegisterUser).unwrap();
    await signIn({ username: values.email, password: accountUser.password });
    const session = await getSessionInfo(responseRegister.data.sso_id).unwrap();

    const { loan_application, client_details, application_condition } = responseRegister.data;
    dispatch(
      setAccountUserPhone({
        id: responseRegister.data.id,
        phone_full: accountUser.phone_full ?? "",
        code_phone: accountUser.code_phone ?? "",
        phone: accountUser.phone ?? "",
      }),
    );
    dispatch(setLoanId(loan_application.id));
    dispatch(setClientDetailsId(client_details.id));
    dispatch(setId_application(Number(loan_application.id_application)));
    dispatch(set_Id_applicationCondition(String(application_condition.id)));
    dispatch(
      setPersonalData({
        id: client_details.id,
        name: values.name,
        first_lastname: values.first_lastname,
        second_lastname: values.second_lastname,
        person_type_id: ownerTypeId,
        rol_detail: "",
        cell_phone_verified: false,
        country_code: accountUser.code_phone ?? "",
        phone: accountUser.phone ?? "",
      }),
    );

    await saveOfficialData(loan_application.id, client_details.id, values);
    return { loanApplicationId: loan_application.id, config: session.data.status_config };
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      name: clientDetails.name || "",
      first_lastname: clientDetails.first_lastname || "",
      second_lastname: clientDetails.second_lastname || "",
      email: accountUser.email || "",
      date_birth: clientDetails.birth_date || "",
      birth_state: clientDetails.birth_state || "",
      sex: clientDetails.sex || "",
      rfc: clientDetails.rfc || "",
      curp: clientDetails.curp || "",
      declaration: false,
      ...datosGuardados,
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        setSubmitting(true);

        let config = statusConfig;
        let loanApplicationId = application.id;
        if (isRegistered) {
          await patchAccount({
            accountId: clientDetails.id,
            body: { name: values.name, last_name: values.first_lastname, last_name_2: values.second_lastname },
          }).unwrap();
          await saveOfficialData(application.id, clientDetails.id, values);
          dispatch(
            setPersonalData({
              ...clientDetails,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
            }),
          );
        } else {
          ({ loanApplicationId, config } = await register(values));
        }
        await saveLeasing(loanApplicationId);

        // Sin ruta fija: se avanza al status que sigue al de esta pantalla (CSRUS) en el `status_config` de la sesión
        // (el que regresa el registro o el del store), así que el back decide la siguiente pantalla (hoy, la validación
        // de teléfono). Los datos oficiales (CSDOF) se guardan aquí, pero no son paso de su secuencia.
        const nextStatus = getNextStatus(config, STATUS_CATALOG.registro_datos_personales.code, [STATUS_SIN_COBERTURA]);
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }
        await triggerPostNextStage({
          applicationId: loanApplicationId,
          statusCode: nextStatus.code,
          username: `TOFU - ${values.email}`,
        }).unwrap();
        // Como `RegistroDatosPersonales`: si sigue la validación de teléfono y el celular no está verificado, se manda el
        // código por SMS (esa pantalla solo lo reenvía).
        if (nextStatus.code === STATUS_CATALOG.validacion_telefono.code && !clientDetails.cell_phone_verified) {
          await requestPhoneCode({ country_code: accountUser.code_phone, phone_number: accountUser.phone }).unwrap();
        }
        sessionStorage.removeItem(DATOS_GUARDADOS_KEY);
        navigate(nextStatus.url.startsWith("/") ? nextStatus.url : `/${nextStatus.url}`);
      } catch (error) {
        const api_error = error as ApiError;

        // Mismos códigos que el legacy (`tofu/pm/RegistroDatosPersonales`).
        if (api_error.status && api_error.data) {
          // El celular de la cuenta ya existe: se guardan los datos y se le pide regresar a cambiarlo.
          if (api_error.data.code === ERROR_CELULAR_REGISTRADO) {
            sessionStorage.setItem(DATOS_GUARDADOS_KEY, JSON.stringify(values));
            setCelularRegistrado(true);
            return;
          }
          if (api_error.data.code === "101") {
            formik.setFieldError("email", "Correo inválido, dominio no permitido, intenta con otro correo");
            return;
          }
          if (api_error.data.code === "001") {
            formik.setFieldError("email", "El correo ya se encuentra registrado, intenta con otro correo");
            return;
          }
          if (api_error.data.code === "000") {
            formik.setFieldError("rfc", `Existe una cuenta con RFC - ${values.rfc}`);
            return;
          }
        }

        Sentry.captureException(error, {
          extra: { component: "RegistroDatosPersonalesWuWei-submit", values: formik.values },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { setFieldValue } = formik;

  // Catálogos: estados (lugar de nacimiento) y tipos de persona.
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoadingInitial(true);
        const [responseStates, personTypes] = await Promise.all([getStates().unwrap(), getPersonTypes().unwrap()]);
        setStates(responseStates);
        const idOf = (code: string) => personTypes.find((item) => item.code === code)?.id;
        setOwnerTypeId(String(idOf(PERSON_CATALOG.dueno_pfae.code) ?? ""));
        setPersonTypeIds(
          [idOf(PERSON_CATALOG.accionista_mayoritario.code), idOf(PERSON_CATALOG.representante_legal.code)]
            .filter(Boolean)
            .map(String),
        );
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "RegistroDatosPersonalesWuWei-initial_fetch" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoadingInitial(false);
      }
    };
    fetchInitialData();
  }, [getStates, getPersonTypes, dispatch]);

  // CURP y primeras 10 posiciones del RFC calculadas con los datos personales (`calcularCurp`, `calcularRfcPf`): el
  // servicio `getCurp` pide sesión y aquí todavía no la hay (la cuenta se crea al continuar). Solo se recalcula cuando
  // cambian esos datos, para no pisar lo que ya venía guardado ni lo que el usuario corrigió después.
  const { name, first_lastname, second_lastname, date_birth, birth_state, sex, rfc } = formik.values;
  const datosClave = [name, first_lastname, second_lastname, date_birth, birth_state, sex].join("|");
  const ultimoCalculo = useRef(formik.initialValues.curp ? datosClave : "");
  useEffect(() => {
    if (datosClave === ultimoCalculo.current) return;
    const datos = {
      nombres: name,
      apellidoPaterno: first_lastname,
      apellidoMaterno: second_lastname,
      fechaNacimiento: date_birth,
    };
    const curp = calcularCurp({ ...datos, sexo: sex, estadoNacimiento: birth_state });
    if (!curp) return;
    ultimoCalculo.current = datosClave;
    setFieldValue("curp", curp);
    // La homoclave la captura el usuario: se conserva si las 10 posiciones calculadas no cambiaron.
    const rfc10 = calcularRfcPf(datos);
    if (!rfc.startsWith(rfc10)) setFieldValue("rfc", rfc10);
    // Solo cuando cambian los datos con los que se calcula.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosClave]);

  const handleUppercase = (field: "rfc" | "curp") => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFieldValue(field, e.target.value.toUpperCase().replace(/\s/g, ""));

  const errorOf = (field: keyof FormValues) => (formik.touched[field] && (formik.errors[field] as string)) || "";
  const disabled = formik.isSubmitting || isLoadingInitial;

  return (
    <Box>
      <PageHeader
        eyebrow="Tus datos"
        title="Cuéntanos de ti"
        subtitle="Con tus datos oficiales calculamos tu RFC y CURP."
      />

      <Paper variant="lendia_paper">
        <SectionHeader label="¿Cómo te llamas?" first />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: "12px 14px" }}>
          <FieldWrapper label="Nombre(s)" error={errorOf("name")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="name"
              value={formik.values.name}
              onChange={(e) => setFieldValue("name", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              placeholder="Nombre(s)"
              inputProps={{ maxLength: 100, "data-testid": "tus-datos-nombre-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
          <FieldWrapper label="Apellido paterno" error={errorOf("first_lastname")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="first_lastname"
              value={formik.values.first_lastname}
              onChange={(e) => setFieldValue("first_lastname", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              placeholder="Apellido paterno"
              inputProps={{ maxLength: 100, "data-testid": "tus-datos-apellido-paterno-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
          <FieldWrapper label="Apellido materno" optionalSuffix="(opcional)" error={errorOf("second_lastname")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="second_lastname"
              value={formik.values.second_lastname}
              onChange={(e) => setFieldValue("second_lastname", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              placeholder="Apellido materno"
              inputProps={{ maxLength: 100, "data-testid": "tus-datos-apellido-materno-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, color: "tokens.textTertiary" }}>
          Como aparecen en tu INE.
        </Typography>

        <SectionHeader label="Contacto" />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px 14px" }}>
          <FieldWrapper
            label="Correo electrónico"
            full
            hint="Aquí te enviaremos tu oferta y tus contratos."
            error={errorOf("email")}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="email"
              type="email"
              value={formik.values.email}
              onChange={(e) => setFieldValue("email", e.target.value.toLowerCase().trim())}
              onBlur={formik.handleBlur}
              placeholder="tu@correo.com"
              inputProps={{ maxLength: 100, "data-testid": "tus-datos-correo-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
        </Box>

        <SectionHeader label="Datos de nacimiento" />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px 14px" }}>
          <FieldWrapper label="Fecha de nacimiento" error={errorOf("date_birth")}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <DatePicker
                value={selectedDate}
                onChange={(date) => {
                  setSelectedDate(date);
                  setFieldValue("date_birth", date ? dayjs(date).format("YYYY-MM-DD") : "");
                }}
                minDate={getFechaNacimientoMinima()}
                maxDate={MAX_DATE}
                slotProps={{
                  textField: {
                    size: "lendia_input",
                    fullWidth: true,
                    name: "date_birth",
                    onBlur: formik.handleBlur,
                    inputProps: { "data-testid": "tus-datos-fecha-nacimiento-input" },
                  },
                }}
                disabled={disabled}
              />
            </LocalizationProvider>
          </FieldWrapper>
          <FieldWrapper label="Lugar de nacimiento" error={errorOf("birth_state")}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              select
              fullWidth
              name="birth_state"
              SelectProps={{ displayEmpty: true }}
              value={formik.values.birth_state}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={{ "data-testid": "tus-datos-lugar-nacimiento-select" }}
              disabled={disabled}
            >
              <MenuItem value="">Selecciona…</MenuItem>
              {states.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </FieldWrapper>
          <FieldWrapper label="Sexo" full hint="Como aparece en tu INE." error={errorOf("sex")}>
            <ChoiceToggle
              variant="radioRow"
              value={formik.values.sex}
              onChange={(value) => setFieldValue("sex", value)}
              options={[
                { value: "MASCULINO", label: "Masculino" },
                { value: "FEMENINO", label: "Femenino" },
              ]}
              disabled={disabled}
              testId="tus-datos-sexo"
            />
          </FieldWrapper>
        </Box>

        <SectionHeader label="Confirma tu RFC y CURP" />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: "12px 14px" }}>
          <FieldWrapper
            label="RFC con homoclave"
            hint="13 caracteres. Calculamos los primeros 10 con tus datos; agrega tu homoclave y corrígelo si no coincide."
            error={errorOf("rfc")}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="rfc"
              value={formik.values.rfc}
              onChange={handleUppercase("rfc")}
              onBlur={formik.handleBlur}
              placeholder="HECJ900812K3A"
              inputProps={{ maxLength: 13, "data-testid": "tus-datos-rfc-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
          <FieldWrapper
            label="CURP"
            hint="Calculada con tus datos de nacimiento; corrígela si no coincide."
            error={errorOf("curp")}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="curp"
              value={formik.values.curp}
              onChange={handleUppercase("curp")}
              onBlur={formik.handleBlur}
              placeholder="HECJ900812HDFRRN08"
              inputProps={{ maxLength: 18, "data-testid": "tus-datos-curp-input" }}
              disabled={disabled}
            />
          </FieldWrapper>
        </Box>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <ConsentRow
          tone="info"
          checked={formik.values.declaration}
          onChange={(checked) => setFieldValue("declaration", checked)}
          disabled={disabled}
          label={
            <>
              <strong>Declaro bajo protesta de decir verdad</strong> que soy el solicitante y que el RFC y CURP son
              correctos.
            </>
          }
        />
      </Box>

      <DialogConfirm
        open={celularRegistrado}
        title="Este celular ya está registrado"
        message={
          <>
            Ya existe una cuenta con el celular <strong>{accountUser.phone_full}</strong>. Regresa para cambiarlo; los
            datos que capturaste se quedan guardados.
          </>
        }
        confirmLabel="Cambiar celular"
        onConfirm={handleBack}
        onClose={handleBack}
        confirmTestId="tus-datos-celular-registrado-button"
      />

      <StepActions
        onBack={handleBack}
        onNext={formik.submitForm}
        backLabel="Atrás"
        nextDisabled={!formik.isValid || disabled || !ownerTypeId}
        backTestId="tus-datos-back-button"
        nextTestId="tus-datos-continue-button"
      />
    </Box>
  );
};
